import { useMemo } from 'react'
import {
  FetchPrescriptiveModel,
  GqlModelTypeFragment,
  ModelInputTag,
} from 'src/services'
import { ActiveCredits, NEW, RUNNING, TRAINING } from 'models/model/model.utils'
import { Spinner } from 'src/components/ui'
import { useLatestRecomendation } from 'models/api'
import { ErrorDisplay } from 'pages/app'
import { useTags } from 'tags/api'
import {
  CreditsBanner,
  ModelHeader,
  ModelPageContainer,
  NewModelInfo,
} from '../../components'
import {
  TargetTag,
  InputTags,
  OtherFactors,
  RecommendedImprovements,
  ModelEvaluation,
} from './sections'
import {
  hasModelEvaluationData,
  modelEvaluationStates,
} from './prescriptive-model.utils'

type Props = {
  model: FetchPrescriptiveModel
  modelTypes: GqlModelTypeFragment[]
  inputTags: ModelInputTag[]
  availableCredits: ActiveCredits
}

export function PrescriptiveModel({
  model,
  modelTypes,
  inputTags,
  availableCredits,
}: Props): JSX.Element {
  const controllableInputs = useMemo(
    () => inputTags.filter(t => t.isControllable),
    [inputTags],
  )

  const uncontrollableInputs = useMemo(
    () => inputTags.filter(t => !t.isControllable),
    [inputTags],
  )

  const latestRecomendationQuery = useLatestRecomendation(
    model.id,
    model.state === RUNNING,
  )
  const tagsQuery = useTags()

  if (
    (model.state === RUNNING && latestRecomendationQuery.isLoading) ||
    tagsQuery.isLoading
  ) {
    return <Spinner />
  }

  if (tagsQuery.isError) {
    return <ErrorDisplay error={tagsQuery.error} action={tagsQuery.refetch} />
  }

  const shouldShowEvaluation =
    modelEvaluationStates.includes(model.state) && hasModelEvaluationData(model)

  return (
    <ModelPageContainer>
      <CreditsBanner model={model} availableCredits={availableCredits} />
      {model.state === NEW && <NewModelInfo />}
      <ModelHeader
        model={model}
        modelTypes={modelTypes}
        availableCredits={availableCredits}
      />
      {shouldShowEvaluation && <ModelEvaluation model={model} />}
      <TargetTag model={model} />
      {[NEW, TRAINING].includes(model.state) ? (
        <InputTags
          model={model}
          inputTags={inputTags}
          tags={tagsQuery.data}
          disabled={model.state !== NEW}
        />
      ) : (
        <>
          <RecommendedImprovements
            model={model}
            controllableInputTags={controllableInputs}
            latestRecomendations={latestRecomendationQuery.data}
          />
          <OtherFactors
            model={model}
            uncontrollableInputTags={uncontrollableInputs}
            controllableInputTags={controllableInputs}
          />
        </>
      )}
    </ModelPageContainer>
  )
}
